import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  History as HistoryIcon,
  Restore as RestoreIcon,
  SaveAlt as StoreIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { api } from '../services/api';
import { filterLastKnownGoodEligible, isLastKnownGoodEligible, LKG_INELIGIBLE_MSG } from '../utils/lkgUtils';

const RESTORE_FILTER_NOTE = 'Snapshots are adapted to current radios and interfaces — not a byte-for-byte rollback.';

const formatStoredAt = (storedAt) => {
  if (!storedAt) return '';
  const d = new Date(storedAt);
  if (Number.isNaN(d.getTime())) return String(storedAt);
  return d.toLocaleString();
};

const LastKnownGoodSelectionPanel = ({ devices = [], onNotify }) => {
  const eligibleFromSelection = useMemo(() => filterLastKnownGoodEligible(devices), [devices]);
  const ineligibleCount = devices.length - eligibleFromSelection.length;
  const [excludedSerials, setExcludedSerials] = useState(() => new Set());

  const activeDevices = useMemo(
    () => eligibleFromSelection.filter((device) => {
      const serial = device.serial_number?.toLowerCase();
      return serial && !excludedSerials.has(serial);
    }),
    [eligibleFromSelection, excludedSerials],
  );

  useEffect(() => {
    setExcludedSerials((prev) => (prev.size === 0 ? prev : new Set()));
  }, [devices]);

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [statusBySerial, setStatusBySerial] = useState({});
  const [viewOpen, setViewOpen] = useState(false);
  const [viewSerial, setViewSerial] = useState('');
  const [viewJson, setViewJson] = useState('');
  const [viewStoredAt, setViewStoredAt] = useState('');
  const [viewLoading, setViewLoading] = useState(false);

  const notify = useCallback((payload) => {
    if (typeof onNotify === 'function') {
      onNotify(payload);
    }
  }, [onNotify]);

  const loadStatuses = useCallback(async () => {
    if (activeDevices.length === 0) {
      setStatusBySerial({});
      return;
    }
    const wanted = new Set(
      activeDevices.map((d) => d.serial_number?.toLowerCase()).filter(Boolean),
    );
    setLoading(true);
    try {
      const all = await api.getAllLastKnownGoodStatus();
      const next = {};
      (all?.devices || []).forEach((entry) => {
        const serial = entry.serial?.toLowerCase();
        if (!serial || !wanted.has(serial)) return;
        next[serial] = {
          available: !!entry.available,
          stored_at: entry.stored_at,
        };
      });
      wanted.forEach((serial) => {
        if (!next[serial]) {
          next[serial] = { available: false };
        }
      });
      setStatusBySerial(next);
    } catch {
      const fallback = {};
      wanted.forEach((serial) => {
        fallback[serial] = { available: false };
      });
      setStatusBySerial(fallback);
    } finally {
      setLoading(false);
    }
  }, [activeDevices]);

  useEffect(() => {
    loadStatuses();
  }, [loadStatuses]);

  const withSnapshot = activeDevices.filter(
    (d) => statusBySerial[d.serial_number?.toLowerCase()]?.available,
  ).length;

  const handleRemoveDevice = (serial) => {
    const key = serial?.toLowerCase();
    if (!key) return;
    setExcludedSerials((prev) => new Set(prev).add(key));
  };

  const handleRestoreList = () => {
    setExcludedSerials(new Set());
  };

  const handleStoreAll = async () => {
    const serials = activeDevices.map((d) => d.serial_number?.toLowerCase()).filter(Boolean);
    if (serials.length === 0) {
      notify({ type: 'error', message: 'No devices in the apply list' });
      return;
    }
    setActionLoading('store-all');
    try {
      const resp = await api.storeLastKnownGoodBatch(serials);
      const ok = resp?.succeeded ?? 0;
      notify({
        type: ok === serials.length ? 'success' : 'warning',
        message: `Stored last known good for ${ok}/${serials.length} device(s)${
          resp?.skipped ? ` (${resp.skipped} skipped)` : ''
        }`,
      });
      await loadStatuses();
    } catch (err) {
      notify({ type: 'error', message: err.message || 'Failed to store last known good' });
    } finally {
      setActionLoading('');
    }
  };

  const handleRestoreAll = async () => {
    const serials = activeDevices
      .map((d) => d.serial_number?.toLowerCase())
      .filter((serial) => statusBySerial[serial]?.available);
    if (serials.length === 0) {
      notify({ type: 'error', message: 'No selected devices have a stored last known good snapshot' });
      return;
    }
    setActionLoading('restore-all');
    try {
      const resp = await api.restoreLastKnownGoodBatch(serials);
      const ok = resp?.succeeded ?? 0;
      notify({
        type: ok === serials.length ? 'success' : 'warning',
        message: `Restored last known good for ${ok}/${serials.length} device(s)${
          resp?.skipped ? ` (${resp.skipped} skipped)` : ''
        }`,
      });
      await loadStatuses();
    } catch (err) {
      notify({ type: 'error', message: err.message || 'Failed to restore last known good' });
    } finally {
      setActionLoading('');
    }
  };

  const handleStoreOne = async (serial) => {
    setActionLoading(`store-${serial}`);
    try {
      await api.storeLastKnownGood(serial);
      notify({ type: 'success', message: `Stored last known good for ${serial}` });
      await loadStatuses();
    } catch (err) {
      notify({ type: 'error', message: err.message || `Failed to store for ${serial}` });
    } finally {
      setActionLoading('');
    }
  };

  const handleRestoreOne = async (serial) => {
    if (!statusBySerial[serial]?.available) {
      notify({ type: 'error', message: `No last known good snapshot for ${serial}` });
      return;
    }
    setActionLoading(`restore-${serial}`);
    try {
      await api.restoreLastKnownGood(serial);
      notify({ type: 'success', message: `Restored last known good for ${serial}` });
    } catch (err) {
      notify({ type: 'error', message: err.message || `Failed to restore for ${serial}` });
    } finally {
      setActionLoading('');
    }
  };

  const handleViewOne = async (serial) => {
    setViewSerial(serial);
    setViewOpen(true);
    setViewLoading(true);
    setViewJson('');
    setViewStoredAt('');
    try {
      const status = await api.getLastKnownGoodStatus(serial, { includeConfig: true });
      if (!status?.available || !status.configuration) {
        notify({ type: 'error', message: `No last known good snapshot for ${serial}` });
        setViewOpen(false);
        return;
      }
      setViewStoredAt(formatStoredAt(status.stored_at));
      setViewJson(JSON.stringify(status.configuration, null, 2));
    } catch (err) {
      notify({ type: 'error', message: err.message || `Failed to load snapshot for ${serial}` });
      setViewOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  if (devices.length === 0) {
    return <Alert severity="info">No devices selected.</Alert>;
  }

  return (
    <Box>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon sx={{ color: '#1565c0' }} />
            Last Known Good
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Store or restore configuration snapshots for jnet2 CAP and Node devices in your apply list.
            Remove devices you do not want to include. {RESTORE_FILTER_NOTE}
          </Typography>

          {ineligibleCount > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {ineligibleCount} selected device(s) are not jnet2 CAP/Node and are excluded. {LKG_INELIGIBLE_MSG}
            </Alert>
          )}

          {eligibleFromSelection.length === 0 ? (
            <Alert severity="warning">{LKG_INELIGIBLE_MSG}</Alert>
          ) : activeDevices.length === 0 ? (
            <Alert
              severity="info"
              action={(
                <Button color="inherit" size="small" onClick={handleRestoreList}>
                  Restore list
                </Button>
              )}
              sx={{ mb: 2 }}
            >
              All eligible devices were removed from the apply list.
            </Alert>
          ) : (
            <>
              <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <Chip label={`${activeDevices.length} in apply list`} size="small" color="primary" />
                {excludedSerials.size > 0 && (
                  <Chip label={`${excludedSerials.size} removed`} size="small" variant="outlined" />
                )}
                <Chip label={`${withSnapshot} with snapshot`} size="small" variant="outlined" />
                {excludedSerials.size > 0 && (
                  <Button size="small" onClick={handleRestoreList} sx={{ ml: 'auto' }}>
                    Restore all to list
                  </Button>
                )}
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 3 }}>
                <Button
                  variant="contained"
                  startIcon={actionLoading === 'store-all' ? <CircularProgress size={16} color="inherit" /> : <StoreIcon />}
                  disabled={!!actionLoading || loading || activeDevices.length === 0}
                  onClick={handleStoreAll}
                >
                  Store for all in list
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={actionLoading === 'restore-all' ? <CircularProgress size={16} color="inherit" /> : <RestoreIcon />}
                  disabled={!!actionLoading || loading || withSnapshot === 0}
                  onClick={handleRestoreAll}
                >
                  Restore all with snapshot
                </Button>
              </Stack>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Stack spacing={1.5}>
                  {activeDevices.map((device) => {
                    const serial = device.serial_number?.toLowerCase();
                    const status = statusBySerial[serial] || {};
                    const busy = actionLoading === `store-${serial}` || actionLoading === `restore-${serial}`;

                    return (
                      <Card key={serial} variant="outlined" sx={{ backgroundColor: '#fafafa' }}>
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              <Tooltip title="Remove from apply list">
                                <IconButton
                                  size="small"
                                  aria-label={`Remove ${serial} from apply list`}
                                  disabled={busy || !!actionLoading}
                                  onClick={() => handleRemoveDevice(serial)}
                                  sx={{ mt: -0.5 }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Box>
                                <Typography variant="subtitle2">{device.name || serial}</Typography>
                                {device.name && (
                                  <Typography variant="caption" color="text.secondary">{serial}</Typography>
                                )}
                                <Box sx={{ mt: 0.5 }}>
                                  {status.available ? (
                                    <Chip
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      label={formatStoredAt(status.stored_at) ? `Snapshot: ${formatStoredAt(status.stored_at)}` : 'Snapshot available'}
                                    />
                                  ) : (
                                    <Chip size="small" variant="outlined" label="No snapshot" />
                                  )}
                                </Box>
                              </Box>
                            </Box>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              {status.available && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<ViewIcon />}
                                  disabled={busy || !!actionLoading}
                                  onClick={() => handleViewOne(serial)}
                                >
                                  View
                                </Button>
                              )}
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={actionLoading === `store-${serial}` ? <CircularProgress size={14} color="inherit" /> : <StoreIcon />}
                                disabled={busy || !!actionLoading}
                                onClick={() => handleStoreOne(serial)}
                              >
                                Store
                              </Button>
                              <Button
                                size="small"
                                color="warning"
                                variant="outlined"
                                startIcon={actionLoading === `restore-${serial}` ? <CircularProgress size={14} color="inherit" /> : <RestoreIcon />}
                                disabled={busy || !!actionLoading || !status.available}
                                onClick={() => handleRestoreOne(serial)}
                              >
                                Restore
                              </Button>
                            </Stack>
                          </Box>
                        </CardContent>
                      </Card>
                    );
                  })}
                </Stack>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          Last known good — {viewSerial}
          {viewStoredAt ? ` (stored ${viewStoredAt})` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {viewLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TextField
              fullWidth
              multiline
              minRows={16}
              maxRows={28}
              value={viewJson}
              InputProps={{ readOnly: true }}
              sx={{ fontFamily: 'monospace', '& textarea': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LastKnownGoodSelectionPanel;
export { isLastKnownGoodEligible };
